import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth";
import { FileText, Settings as SettingsIcon, CreditCard, ShieldCheck, LogOut, Plus, Wallet, Menu, X, CalendarDays, FileStack, ReceiptText } from "lucide-react";
import InstallApp from "@/components/InstallApp";
import NotificationBell from "@/components/NotificationBell";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const NAV = [
  { to: "/app/oferte", key: "offers", icon: FileText, testid: "nav-offers" },
  { to: "/app/lucrari", key: "jobs", icon: Wallet, testid: "nav-jobs" },
  { to: "/app/calendar", key: "calendar", icon: CalendarDays, testid: "nav-calendar" },
  { to: "/app/documente", key: "documents", icon: FileStack, testid: "nav-documents" },
  { to: "/app/facturare", key: "invoicing", icon: ReceiptText, testid: "nav-invoicing" },
  { to: "/app/setari", key: "settings", icon: SettingsIcon, testid: "nav-settings" },
  { to: "/app/abonament", key: "billing", icon: CreditCard, testid: "nav-billing" },
];

const linkClass = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors border-l-2 ${isActive ? "bg-[#111C30] text-white border-[#00BFFF]" : "text-[#94A3B8] hover:bg-[#0B111D] hover:text-[#E2E8F0] border-transparent"}`;

function NavLinks({ isAdmin, onNavigate, testidSuffix = "" }) {
  const { t } = useTranslation();
  return (
    <nav className="p-3 flex-1">
      <Link to="/app/oferte/noua" onClick={onNavigate} data-testid={`sidebar-new-offer${testidSuffix}`} className="btn-primary w-full flex items-center justify-center gap-2 mb-4">
        <Plus size={16} /> {t("nav.newOffer")}
      </Link>
      {NAV.map((n) => (
        <NavLink key={n.to} to={n.to} onClick={onNavigate} data-testid={`${n.testid}${testidSuffix}`} className={linkClass}>
          <n.icon size={18} /> {t(`nav.${n.key}`)}
        </NavLink>
      ))}
      {isAdmin && (
        <NavLink to="/app/admin" onClick={onNavigate} data-testid={`nav-admin${testidSuffix}`} className={linkClass}>
          <ShieldCheck size={18} /> {t("nav.admin")}
        </NavLink>
      )}
    </nav>
  );
}

function UserFooter({ user, planName, onLogout, testidSuffix = "" }) {
  const { t } = useTranslation();
  return (
    <div className="p-4 border-t border-[#1E293B]">
      <div className="mb-3"><LanguageSwitcher className="w-full" /></div>
      <div className="mb-3"><InstallApp variant="button" /></div>
      <div className="text-xs text-[#94A3B8]">{t("nav.connectedAs")}</div>
      <div className="font-semibold text-sm truncate" data-testid={`user-name${testidSuffix}`}>{user.full_name}</div>
      <div className="text-xs text-[#94A3B8] truncate">{user.email}</div>
      <div className="mt-3 flex items-center justify-between">
        <span className="font-mono-plex text-xs px-2 py-1 bg-[#111C30] text-white" data-testid={`user-plan${testidSuffix}`}>{planName}</span>
        <button onClick={onLogout} className="text-xs text-[#94A3B8] hover:text-[#EF4444] flex items-center gap-1" data-testid={`logout-btn${testidSuffix}`}>
          <LogOut size={14} /> {t("nav.logout")}
        </button>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  if (!user) return null;
  const planName = { free: "Free", basic: "Basic", pro: "Pro", business: "Business", firma: "Business" }[user.plan] || "Free";
  const doLogout = () => { setMenuOpen(false); logout(); nav("/"); };

  return (
    <div className="min-h-screen flex bg-[#0B111D] text-[#E2E8F0]">
      {/* Desktop sidebar */}
      <aside className="w-64 bg-[#0B111D] border-r border-[#1E293B] flex-col hidden md:flex">
        <div className="px-6 py-5 flex items-center justify-between border-b border-[#1E293B]">
          <Link to="/" className="flex items-center gap-2">
            <img src="/brand/logo-icon.png" alt="IronOfferAI" className="w-8 h-8 rounded-lg shadow-sm" />
            <span className="font-display font-extrabold text-lg tracking-tight">IronOfferAI</span>
          </Link>
          <NotificationBell />
        </div>
        <NavLinks isAdmin={user.is_admin} />
        <UserFooter user={user} planName={planName} onLogout={doLogout} />
      </aside>

      {/* Mobile header */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-30 glass-header">
        <div className="h-14 flex items-center justify-between px-4">
          <button onClick={() => setMenuOpen(true)} data-testid="mobile-menu-btn" className="p-2 -ml-2 text-[#E2E8F0]" aria-label={t("nav.menu")}>
            <Menu size={24} />
          </button>
          <Link to="/" className="flex items-center gap-2 font-display font-extrabold">
            <img src="/brand/logo-icon.png" alt="IronOfferAI" className="w-7 h-7 rounded-md" />
            IronOfferAI
          </Link>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <span className="font-mono-plex text-[10px] px-2 py-1 bg-[#111C30] text-white rounded">{planName}</span>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMenuOpen(false)} />
          <div className="absolute top-0 left-0 bottom-0 w-72 max-w-[85%] bg-[#0B111D] border-r border-[#1E293B] flex flex-col safe-pt">
            <div className="px-5 py-4 flex items-center justify-between border-b border-[#1E293B]">
              <Link to="/" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 font-display font-extrabold">
                <img src="/brand/logo-icon.png" alt="IronOfferAI" className="w-8 h-8 rounded-lg" />
                IronOfferAI
              </Link>
              <button onClick={() => setMenuOpen(false)} data-testid="mobile-menu-close" className="p-2 text-[#94A3B8] hover:text-[#E2E8F0]" aria-label={t("nav.close")}>
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <NavLinks isAdmin={user.is_admin} onNavigate={() => setMenuOpen(false)} testidSuffix="-mobile" />
            </div>
            <UserFooter user={user} planName={planName} onLogout={doLogout} testidSuffix="-mobile" />
          </div>
        </div>
      )}

      <main className="flex-1 md:pt-0 pt-mobile-header pb-32 md:pb-24">
        <Outlet />
      </main>
    </div>
  );
}
